import { useLayoutEffect, useMemo, useRef, useState } from "preact/hooks";
import type { MapDocument, MapGeometry, MapLocation } from "../shared/types";
import type { VenueMapPinGroup } from "./VenueMapPins";
import type { VenueMapRouteData } from "./VenueMapRoute";
import { fitVenueMapRoute } from "./venueMapRouteFit";
import { fitVenueMap } from "./venueMapFit";
import { venueMapBounds } from "./venueMapBounds";
import {
  focusVenueMapLocation,
  type MapFocusPoint,
  type MapFocusTarget,
} from "./venueMapFocus";
import { venueMapPinAnchor } from "./venueMapPinAnchor";
import {
  projectMapPoint,
  projectMapDepth,
  unprojectMapPoint,
  mapStandHeight,
  type VenueMapMode,
} from "./venueMapProjection";

type Point = { x: number; y: number };
type View = {
  width: number;
  height: number;
  zoom: number;
  rotation: number;
  pitch: number;
  fitRotation: number;
  fitPitch: number;
  fitVersion: number;
  mode: VenueMapMode;
  focusTarget?: MapFocusTarget;
  framingScale?: number;
};
type Contact = Point & {
  start: Point;
  started: number;
  moved: boolean;
  pin: boolean;
  dragAnchor?: Point | null;
  rotateDrag?: { rotation: number; pitch: number; anchor: Point };
};

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const DRAG_ROTATION_PER_PIXEL = 0.5;
const DRAG_TILT_PER_PIXEL = 0.3;
const clampPitch = (pitch: number) => Math.min(70, Math.max(0, pitch));
const clampZoom = (zoom: number) =>
  Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));
const distance = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y);
const angle = (a: Point, b: Point) =>
  (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
const midpoint = (a: Point, b: Point) => ({
  x: (a.x + b.x) / 2,
  y: (a.y + b.y) / 2,
});

function perspectiveBounds(
  aspect: number,
  rotation: number,
  pitch: number,
  geometry: MapGeometry,
) {
  const camera = {
    sourceWidth: geometry.width,
    sourceHeight: geometry.height,
    sourceElevation: venueMapBounds(geometry).elevation,
    imageWidth: geometry.width,
    imageHeight: geometry.width / aspect,
    stageWidth: 0,
    stageHeight: 0,
    rotation,
    pitch,
    perspective: true,
  };
  // Frame the actual floor, including the slab and tallest roof. Its projected
  // bounds size the scroll area; they never change the camera's optical centre.
  const corners = venueMapBounds(geometry).points.map((point) => {
    const projected = projectMapPoint(camera, point);
    return {
      x: projected.x / geometry.width,
      y: projected.y / geometry.width,
      perspective: projectMapDepth(camera, point),
    };
  });
  return {
    width: Math.max(...corners.map((point) => Math.abs(point.x))) * 2,
    height: Math.max(...corners.map((point) => Math.abs(point.y))) * 2,
    corners,
  };
}

function dimensions(
  view: View,
  aspect: number,
  maxPins: number,
  fitted: ReturnType<typeof fitVenueMap>,
  geometry: MapGeometry,
) {
  const is3D = view.mode === "3d";
  // Shared locations wrap after three 44px pins. Leave enough camera space
  // for the entire upright callout and its focus outline beside the labels.
  const columns = Math.min(3, Math.max(1, maxPins));
  const rows = Math.ceil(Math.max(1, maxPins) / 3);
  const pinWidth = columns * 44 + (columns - 1) * 3 + (maxPins > 1 ? 6 : 0);
  const pinHeight = rows * 44 + (rows - 1) * 3 + (maxPins > 1 ? 14 : 8);
  const gutterX = pinWidth / 2 + 4;
  const gutterY = pinHeight + 4;
  const pitch = is3D ? view.pitch : 0;
  // Keep the zoom baseline stable while orbiting. Only Fit captures a new
  // orientation to frame, so rotating or tilting never changes the map scale.
  const fittedWidth = is3D
    ? fitted.imageWidth
    : Math.min(
        Math.max(1, view.width - (maxPins > 1 ? gutterX * 2 : 0)),
        Math.max(1, view.height - (maxPins > 1 ? gutterY * 2 : 0)) * aspect,
      );
  const imageWidth = fittedWidth * (view.framingScale ?? 1) * view.zoom;
  const imageHeight = imageWidth / aspect;
  const radians = (view.rotation * Math.PI) / 180;
  const cosine = Math.abs(Math.cos(radians));
  const sine = Math.abs(Math.sin(radians));
  const bounds = perspectiveBounds(aspect, view.rotation, pitch, geometry);
  // Panning happens before perspective division, so a pixel of scroll moves a
  // near booth farther than a distant one. Solve the required travel at each
  // depth so every edge and its full-size pins remain reachable.
  const focusCamera = {
    sourceWidth: geometry.width,
    sourceHeight: geometry.height,
    sourceElevation: venueMapBounds(geometry).elevation,
    imageWidth,
    imageHeight,
    stageWidth: 0,
    stageHeight: 0,
    rotation: view.rotation,
    pitch,
    perspective: is3D,
  };
  const focusPosition = view.focusTarget
    ? projectMapPoint(focusCamera, view.focusTarget.anchor)
    : undefined;
  const focusDepth =
    view.focusTarget && is3D
      ? projectMapDepth(focusCamera, view.focusTarget.anchor)
      : 1;
  // A selection at the floor's edge must still be able to reach the centre.
  // Reserve only the extra travel required for this explicit focus operation.
  const focusPanX =
    focusPosition && view.focusTarget
      ? Math.abs(
          (focusPosition.x - (view.focusTarget.at.x - view.width / 2)) /
            focusDepth,
        )
      : 0;
  const focusPanY =
    focusPosition && view.focusTarget
      ? Math.abs(
          (focusPosition.y - (view.focusTarget.at.y - view.height / 2)) /
            focusDepth,
        )
      : 0;
  const panX = Math.max(
    focusPanX,
    0,
    Math.abs(fitted.panX) * view.zoom,
    ...bounds.corners.map(
      (point) =>
        (Math.abs(point.x) * imageWidth - (view.width / 2 - gutterX)) /
        point.perspective,
    ),
  );
  const panY = Math.max(
    focusPanY,
    0,
    Math.abs(fitted.panY) * view.zoom,
    ...bounds.corners.map(
      (point) =>
        (Math.abs(point.y) * imageWidth - (view.height / 2 - gutterY)) /
        point.perspective,
    ),
  );
  const orientation = ((((view.rotation + 180) % 360) + 360) % 360) - 180;
  // Upright pins can extend beyond the floor bounds. Reserve camera padding,
  // including the raised roofs; the fitted 3D view stays inside this padding.
  const flatGutter = Math.abs(orientation) > 0.01 ? 80 : 0;
  return {
    sourceWidth: geometry.width,
    sourceHeight: geometry.height,
    sourceElevation: venueMapBounds(geometry).elevation,
    imageWidth,
    imageHeight,
    pitch,
    perspective: is3D,
    fitPanX: is3D ? fitted.panX : 0,
    fitPanY: is3D ? fitted.panY : 0,
    stageWidth: is3D
      ? view.width + Math.ceil(panX) * 2
      : Math.max(
          view.width + Math.ceil(focusPanX) * 2,
          imageWidth * cosine +
            imageHeight * sine +
            Math.max(flatGutter, maxPins > 1 ? gutterX : 0) * 2,
        ),
    stageHeight: is3D
      ? view.height + Math.ceil(panY) * 2
      : Math.max(
          view.height + Math.ceil(focusPanY) * 2,
          imageWidth * sine +
            imageHeight * cosine +
            Math.max(flatGutter, maxPins > 1 ? gutterY : 0) * 2,
        ),
  };
}

function cameraAtScroll(
  view: View,
  size: ReturnType<typeof dimensions>,
  scroll: Point,
) {
  const offsetX =
    view.mode === "3d" ? scroll.x + view.width / 2 - size.stageWidth / 2 : 0;
  const offsetY =
    view.mode === "3d" ? scroll.y + view.height / 2 - size.stageHeight / 2 : 0;
  return {
    ...size,
    rotation: view.rotation,
    // The browser subtracts scroll from these stage coordinates, leaving the
    // principal point fixed at the centre of the visible viewport.
    offsetX,
    offsetY,
    panX: -offsetX,
    panY: -offsetY,
  };
}

/** Native scrolling keeps the image and fixed-size stop pins in one coordinate space. */
export function useVenueMapInteraction(
  document: MapDocument,
  groups: readonly VenueMapPinGroup[],
) {
  const latestDocument = useRef(document);
  latestDocument.current = document;
  const geometry = document.geometry;
  const aspect = geometry.width / geometry.height;
  const mapId = document.id;
  const initialRotation = document.camera.rotation;
  const initialPitch = clampPitch(document.camera.pitch);
  const elevations = useMemo(
    () =>
      new Map(
        geometry.stands.map((stand) => [stand.id, mapStandHeight(stand)]),
      ),
    [geometry],
  );
  const viewport = useRef<HTMLDivElement>(null);
  // Playback and translated titles must not reset the camera or its fit cache.
  const pinLayout = JSON.stringify(
    groups.map(({ id, x, y, stops }) => [id, x, y, stops.length]),
  );
  const pins = useMemo(
    () =>
      groups.map((group) => ({
        ...venueMapPinAnchor(group, geometry),
        z: elevations.get(group.id) || 0,
        count: group.stops.length,
      })),
    [pinLayout, geometry, elevations],
  );
  const maxPins = Math.max(1, ...pins.map((pin) => pin.count));
  // Keep the fitted scale stable while a draft is edited. Fit explicitly
  // refreshes it; viewport or configured camera changes calculate a new fit.
  const fitCache = useRef<{
    key: string;
    fitted: ReturnType<typeof fitVenueMap>;
  } | null>(null);
  const cameraConfig = JSON.stringify([mapId, initialRotation, initialPitch]);
  const appliedCameraConfig = useRef("");
  const getDimensions = useMemo(() => {
    // A gesture uses the same immutable view for projection, clamping and
    // rendering. Keep those calculations shared without retaining old views.
    const cache = new WeakMap<View, ReturnType<typeof dimensions>>();
    return (view: View) => {
      let size = cache.get(view);
      if (!size) {
        const key = `${cameraConfig},${view.width},${view.height},${view.fitRotation},${view.fitPitch},${view.fitVersion}`;
        if (key !== fitCache.current?.key) {
          const fitted = fitVenueMap(
            view.width,
            view.height,
            aspect,
            view.fitRotation,
            view.fitPitch,
            pins,
            geometry,
          );
          fitCache.current = { key, fitted };
        }
        size = dimensions(
          view,
          aspect,
          maxPins,
          fitCache.current!.fitted,
          geometry,
        );
        cache.set(view, size);
      }
      return size;
    };
  }, [aspect, maxPins, pins, geometry, cameraConfig]);
  const model = useRef<View>({
    width: 0,
    height: 0,
    zoom: 1,
    rotation: initialRotation,
    pitch: initialPitch,
    fitRotation: initialRotation,
    fitPitch: initialPitch,
    fitVersion: 0,
    mode: "3d",
  });
  const pendingScroll = useRef<Point | null>(null);
  const pendingView = useRef<View | null>(null);
  const cameraAdjustment = useRef<{
    anchor: Point;
    view: View;
    scroll: Point;
  } | null>(null);
  const [view, setView] = useState(model.current);
  const cameraScroll = useRef<Point>({ x: 0, y: 0 });
  const [, setScrollRevision] = useState(0);
  const [interacting, setInteracting] = useState(false);
  const controls = useRef({
    zoom: (_next: number) => {},
    rotate: (_delta: number) => {},
    rotation: (_angle: number) => {},
    tilt: (_pitch: number) => {},
    fit: () => {},
    reset: () => {},
    focus: (_location: MapLocation) => {},
    fitRoute: (_route: VenueMapRouteData, _bottomInset?: number) => {},
    toggleMode: () => {},
  });

  useLayoutEffect(() => {
    const element = viewport.current;
    if (!element) return;
    const resetCamera = appliedCameraConfig.current !== cameraConfig;
    appliedCameraConfig.current = cameraConfig;
    const pointers = new Map<number, Contact>();
    let pinch:
      | {
          distance: number;
          zoom: number;
          anchor: Point;
          angle: number | undefined;
          rotation: number;
        }
      | undefined;
    let lastTap: (Point & { time: number }) | undefined;
    let lastPointerType = "";
    let suppressClick = false;
    let fittedView = resetCamera;
    let safariGesture:
      { zoom: number; rotation: number; anchor: Point } | undefined;
    setInteracting(false);

    const localPoint = (point: Point): Point => {
      const box = element.getBoundingClientRect();
      return { x: point.x - box.left, y: point.y - box.top };
    };
    const center = (): Point => ({
      x: model.current.width / 2,
      y: model.current.height / 2,
    });
    const scrollPosition = (): Point =>
      pendingScroll.current || {
        x: element.scrollLeft,
        y: element.scrollTop,
      };
    const imagePoint = (point: Point): Point => {
      const size = getDimensions(model.current);
      const scroll = scrollPosition();
      const anchor = unprojectMapPoint(
        cameraAtScroll(model.current, size, scroll),
        { x: scroll.x + point.x, y: scroll.y + point.y },
      );
      // Blank space near the perspective horizon can intersect the floor far
      // beyond the venue. Keep gestures there focused on the map itself.
      return model.current.mode === "3d"
        ? {
            x: Math.max(0, Math.min(1, anchor.x)),
            y: Math.max(0, Math.min(1, anchor.y)),
          }
        : anchor;
    };
    const clampScroll = (point: Point, next = model.current): Point => {
      const size = getDimensions(next);
      return {
        x: Math.max(0, Math.min(size.stageWidth - next.width, point.x)),
        y: Math.max(0, Math.min(size.stageHeight - next.height, point.y)),
      };
    };
    const pan = (point: Point) => {
      fittedView = false;
      cameraAdjustment.current = null;
      const next = clampScroll(point);
      if (pendingScroll.current) pendingScroll.current = next;
      element.scrollLeft = next.x;
      element.scrollTop = next.y;
      syncCameraScroll();
    };
    const syncCameraScroll = () => {
      const next = { x: element.scrollLeft, y: element.scrollTop };
      if (
        cameraScroll.current.x !== next.x ||
        cameraScroll.current.y !== next.y
      ) {
        // A viewport resize can clamp native scrolling before ResizeObserver
        // runs. That is not a visitor pan and should retain automatic fitting.
        if (
          !pendingScroll.current &&
          element.clientWidth === model.current.width &&
          element.clientHeight === model.current.height
        )
          fittedView = false;
        cameraScroll.current = next;
        setScrollRevision((revision) => revision + 1);
      }
    };
    const updateView = (next: View, anchor: MapFocusPoint, at: Point) => {
      fittedView = false;
      const size = getDimensions(next);
      const position = projectMapPoint(
        { ...size, rotation: next.rotation },
        anchor,
      );
      const perspective =
        next.mode === "3d"
          ? projectMapDepth({ ...size, rotation: next.rotation }, anchor)
          : 1;
      const scroll = clampScroll(
        {
          x:
            (size.stageWidth - next.width) / 2 +
            (position.x - size.stageWidth / 2 - (at.x - next.width / 2)) /
              perspective,
          y:
            (size.stageHeight - next.height) / 2 +
            (position.y - size.stageHeight / 2 - (at.y - next.height / 2)) /
              perspective,
        },
        next,
      );
      const previous = model.current;
      model.current = next;
      if (
        next.width === previous.width &&
        next.height === previous.height &&
        next.zoom === previous.zoom &&
        next.rotation === previous.rotation &&
        next.pitch === previous.pitch &&
        next.fitRotation === previous.fitRotation &&
        next.fitPitch === previous.fitPitch &&
        next.fitVersion === previous.fitVersion &&
        next.mode === previous.mode &&
        next.focusTarget === previous.focusTarget &&
        next.framingScale === previous.framingScale
      ) {
        pan(scroll);
      } else {
        pendingScroll.current = scroll;
        pendingView.current = next;
        setView(next);
      }
    };
    const zoomAt = (
      zoom: number,
      at: Point,
      anchor = imagePoint(at),
      rotation = model.current.rotation,
      pitch = model.current.pitch,
    ) =>
      updateView(
        {
          ...model.current,
          zoom: clampZoom(zoom),
          rotation,
          pitch: clampPitch(pitch),
        },
        anchor,
        at,
      );
    const fitView = (next: View) => {
      const size = getDimensions(next);
      updateView(
        next,
        { x: 0.5, y: 0.5 },
        { x: next.width / 2 + size.fitPanX, y: next.height / 2 + size.fitPanY },
      );
      fittedView = true;
    };
    const cancelGesture = () => {
      const captured = [...pointers.keys()];
      if (captured.length) suppressClick = true;
      pointers.clear();
      for (const id of captured) {
        if (element.hasPointerCapture(id)) element.releasePointerCapture(id);
      }
      pinch = undefined;
      safariGesture = undefined;
      lastTap = undefined;
      cameraAdjustment.current = null;
      setInteracting(false);
    };
    const fit = () => {
      cancelGesture();
      fitView({
        ...model.current,
        zoom: 1,
        fitRotation: model.current.rotation,
        fitPitch: model.current.pitch,
        fitVersion: model.current.fitVersion + 1,
        focusTarget: undefined,
        framingScale: undefined,
      });
    };
    const reset = () => {
      cancelGesture();
      fitView({
        ...model.current,
        zoom: 1,
        rotation: initialRotation,
        pitch: initialPitch,
        fitRotation: initialRotation,
        fitPitch: initialPitch,
        fitVersion: model.current.fitVersion + 1,
        focusTarget: undefined,
        framingScale: undefined,
        mode: "3d",
      });
    };
    const stepZoom = (at: Point) => {
      if (model.current.zoom >= MAX_ZOOM) zoomAt(1, at);
      else zoomAt(model.current.zoom * 2, at);
    };
    const adjustCamera = (rotation: number, pitch: number) => {
      const scroll = scrollPosition();
      const previous = cameraAdjustment.current;
      // Button presses and slider updates share an orbit centre. Re-intersecting
      // after every native scroll rounding would slowly move that centre.
      const anchor =
        previous?.view === model.current &&
        previous.scroll.x === scroll.x &&
        previous.scroll.y === scroll.y
          ? previous.anchor
          : imagePoint(center());
      zoomAt(model.current.zoom, center(), anchor, rotation, pitch);
      cameraAdjustment.current = {
        anchor,
        view: model.current,
        scroll: scrollPosition(),
      };
    };
    controls.current = {
      zoom: (next) => zoomAt(next, center()),
      rotate: (delta) => {
        if (Number.isFinite(delta))
          adjustCamera(
            ((model.current.rotation % 360) + (delta % 360)) % 360,
            model.current.pitch,
          );
      },
      rotation: (angle) => {
        if (Number.isFinite(angle))
          adjustCamera(angle % 360, model.current.pitch);
      },
      tilt: (pitch) => {
        if (model.current.mode === "3d")
          adjustCamera(model.current.rotation, pitch);
      },
      fit,
      reset,
      focus: (location) => {
        const current = model.current;
        const framing = focusVenueMapLocation(
          latestDocument.current,
          location.id,
          {
            ...getDimensions({ ...current, zoom: 1, framingScale: undefined }),
            rotation: current.rotation,
          },
          current.width,
          current.height,
        );
        if (!framing) return;
        cancelGesture();
        updateView(
          {
            ...current,
            zoom: framing.zoom,
            focusTarget: framing,
            framingScale: undefined,
          },
          framing.anchor,
          framing.at,
        );
      },
      fitRoute: (route, bottomInset = 0) => {
        const current = model.current;
        const next: View = {
          ...current,
          zoom: 1,
          fitRotation: current.rotation,
          fitPitch: current.pitch,
          fitVersion: current.fitVersion + 1,
          focusTarget: undefined,
          framingScale: undefined,
        };
        const framing = fitVenueMapRoute(
          latestDocument.current,
          route,
          groups,
          { ...getDimensions(next), rotation: current.rotation },
          current.width,
          current.height,
          bottomInset,
        );
        if (!framing) return;
        cancelGesture();
        updateView(
          {
            ...next,
            zoom: framing.zoom,
            framingScale: framing.framingScale,
            focusTarget: framing,
          },
          framing.anchor,
          framing.at,
        );
      },
      toggleMode: () => {
        const at = center();
        const anchor = imagePoint(at);
        updateView(
          { ...model.current, mode: model.current.mode === "3d" ? "2d" : "3d" },
          anchor,
          at,
        );
      },
    };
    const measure = () => {
      const previous = model.current;
      const anchor = imagePoint(center());
      const width = element.clientWidth;
      const height = element.clientHeight;
      const next = { ...previous, width, height };
      if (!previous.width || !previous.height || fittedView) {
        fitView(next);
        return;
      }
      updateView(next, anchor, {
        x: width / 2,
        y: height / 2,
      });
    };
    if (resetCamera) {
      model.current = {
        width: 0,
        height: 0,
        zoom: 1,
        rotation: initialRotation,
        pitch: initialPitch,
        fitRotation: initialRotation,
        fitPitch: initialPitch,
        fitVersion: 0,
        mode: "3d",
      };
      pendingScroll.current = null;
      pendingView.current = null;
      cameraAdjustment.current = null;
      cameraScroll.current = { x: 0, y: 0 };
      measure();
    } else if (
      element.clientWidth !== model.current.width ||
      element.clientHeight !== model.current.height
    ) {
      measure();
    }
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    element.addEventListener("scroll", syncCameraScroll, { passive: true });

    const capture = (id: number) => {
      try {
        if (!element.hasPointerCapture(id)) element.setPointerCapture(id);
      } catch (error) {
        // A cancelled contact can disappear before capture is applied.
        if (!(error instanceof DOMException) || error.name !== "NotFoundError")
          throw error;
      }
    };
    const beginPinch = () => {
      const [a, b] = [...pointers.values()];
      pinch = {
        distance: Math.max(8, distance(a, b)),
        zoom: model.current.zoom,
        anchor: imagePoint(localPoint(midpoint(a, b))),
        angle: distance(a, b) >= 8 ? angle(a, b) : undefined,
        rotation: model.current.rotation,
      };
      pointers.forEach((contact, id) => {
        contact.moved = true;
        contact.dragAnchor = undefined;
        contact.rotateDrag = undefined;
        capture(id);
      });
      lastTap = undefined;
      suppressClick = true;
      setInteracting(true);
    };
    const pointerDown = (event: PointerEvent) => {
      if (!pointers.size) suppressClick = false;
      if (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        (event.shiftKey && event.pointerType !== "mouse")
      )
        return;
      lastPointerType = event.pointerType;
      const point = { x: event.clientX, y: event.clientY };
      pointers.set(event.pointerId, {
        ...point,
        start: point,
        started: performance.now(),
        moved: false,
        pin:
          event.target instanceof Element &&
          !!event.target.closest("a, button"),
        // Choose the gesture at mouse-down so releasing Shift during a drag
        // cannot unexpectedly switch from rotation to panning.
        rotateDrag: event.shiftKey
          ? {
              rotation: model.current.rotation,
              pitch: model.current.pitch,
              anchor: imagePoint(center()),
            }
          : undefined,
      });
      if (pointers.size === 2) beginPinch();
    };
    const pointerMove = (event: PointerEvent) => {
      const contact = pointers.get(event.pointerId);
      if (!contact) return;
      const previous = { x: contact.x, y: contact.y };
      contact.x = event.clientX;
      contact.y = event.clientY;
      if (pointers.size >= 2 && pinch) {
        const [a, b] = [...pointers.values()];
        const separation = distance(a, b);
        const nextAngle = separation >= 8 ? angle(a, b) : undefined;
        // Unwrap each increment so crossing ±180° never reverses the twist.
        // Nearly coincident fingers have no stable angle; rebase on separation.
        if (nextAngle !== undefined && pinch.angle !== undefined) {
          pinch.rotation += ((nextAngle - pinch.angle + 540) % 360) - 180;
        }
        pinch.angle = nextAngle;
        zoomAt(
          (pinch.zoom * separation) / pinch.distance,
          localPoint(midpoint(a, b)),
          pinch.anchor,
          pinch.rotation,
        );
        event.preventDefault();
      } else if (contact.moved || distance(contact.start, contact) >= 6) {
        const origin = contact.moved ? previous : contact.start;
        contact.moved = true;
        suppressClick = true;
        lastTap = undefined;
        capture(event.pointerId);
        setInteracting(true);
        if (contact.rotateDrag) {
          zoomAt(
            model.current.zoom,
            center(),
            contact.rotateDrag.anchor,
            contact.rotateDrag.rotation -
              (contact.x - contact.start.x) * DRAG_ROTATION_PER_PIXEL,
            model.current.mode === "3d"
              ? contact.rotateDrag.pitch -
                  (contact.y - contact.start.y) * DRAG_TILT_PER_PIXEL
              : model.current.pitch,
          );
          event.preventDefault();
          return;
        }
        const at = localPoint(origin);
        const scroll = scrollPosition();
        if (model.current.mode === "3d" && contact.dragAnchor === undefined) {
          const anchor = imagePoint(at);
          const position = projectMapPoint(
            cameraAtScroll(model.current, getDimensions(model.current), scroll),
            anchor,
          );
          // Retain one floor point for the whole drag so browser scroll
          // rounding cannot accumulate into drift under the pointer.
          contact.dragAnchor =
            distance(position, { x: scroll.x + at.x, y: scroll.y + at.y }) < 0.5
              ? anchor
              : null;
        }
        if (model.current.mode === "3d" && contact.dragAnchor) {
          updateView(model.current, contact.dragAnchor, localPoint(contact));
        } else {
          // Empty margins may have no safe floor ray. Drag them smoothly
          // without snapping a clamped floor anchor onto the pointer.
          pan({
            x: scroll.x - (contact.x - origin.x),
            y: scroll.y - (contact.y - origin.y),
          });
        }
        event.preventDefault();
      }
    };
    const pointerEnd = (event: PointerEvent) => {
      const contact = pointers.get(event.pointerId);
      if (!contact) return;
      const cancelled = event.type !== "pointerup";
      pointers.delete(event.pointerId);
      if (element.hasPointerCapture(event.pointerId))
        element.releasePointerCapture(event.pointerId);
      if (cancelled) {
        lastTap = undefined;
        suppressClick = true;
      } else if (
        !contact.moved &&
        !contact.pin &&
        event.pointerType === "touch" &&
        performance.now() - contact.started < 300
      ) {
        const now = performance.now();
        if (
          lastTap &&
          now - lastTap.time < 320 &&
          distance(lastTap, contact) < 24
        ) {
          stepZoom(localPoint(contact));
          lastTap = undefined;
          suppressClick = true;
          event.preventDefault();
        } else lastTap = { x: contact.x, y: contact.y, time: now };
      } else lastTap = undefined;
      pinch = undefined;
      if (pointers.size >= 2) beginPinch();
      else if (pointers.size === 1) {
        const remaining = [...pointers.values()][0];
        remaining.start = { x: remaining.x, y: remaining.y };
        remaining.moved = true;
        remaining.dragAnchor = undefined;
        remaining.rotateDrag = undefined;
      } else setInteracting(false);
    };
    const click = (event: MouseEvent) => {
      if (suppressClick && event.detail !== 0) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    const lostCapture = (event: PointerEvent) => {
      // Touch starts with implicit capture on the image or link. Transferring
      // that capture to the viewport must not cancel the active gesture.
      if (
        event.target === element &&
        !element.hasPointerCapture(event.pointerId)
      )
        pointerEnd(event);
    };
    const doubleClick = (event: MouseEvent) => {
      if (
        suppressClick ||
        lastPointerType === "touch" ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        event.shiftKey ||
        (event.target instanceof Element && event.target.closest("a, button"))
      )
        return;
      event.preventDefault();
      stepZoom(localPoint({ x: event.clientX, y: event.clientY }));
    };
    const wheel = (event: WheelEvent) => {
      if (!event.ctrlKey) return;
      event.preventDefault();
      if (safariGesture) return;
      const unit =
        event.deltaMode === 1
          ? 16
          : event.deltaMode === 2
            ? model.current.height
            : 1;
      const delta = Math.max(-100, Math.min(100, event.deltaY * unit));
      zoomAt(
        model.current.zoom * Math.exp(-delta * 0.01),
        localPoint({ x: event.clientX, y: event.clientY }),
      );
    };
    // Safari trackpads expose gesture events; touchscreens use Pointer Events above.
    const gesture = (event: Event) => {
      if (pointers.size >= 2) return;
      event.preventDefault();
      const { scale, rotation, clientX, clientY } = event as Event & {
        scale: number;
        rotation: number;
        clientX: number;
        clientY: number;
      };
      const at =
        Number.isFinite(clientX) && Number.isFinite(clientY)
          ? localPoint({ x: clientX, y: clientY })
          : center();
      if (event.type === "gesturestart") {
        safariGesture = {
          zoom: model.current.zoom,
          rotation: model.current.rotation,
          anchor: imagePoint(at),
        };
        setInteracting(true);
      } else if (
        event.type === "gesturechange" &&
        safariGesture &&
        Number.isFinite(scale) &&
        scale > 0
      ) {
        zoomAt(
          safariGesture.zoom * scale,
          at,
          safariGesture.anchor,
          safariGesture.rotation + (Number.isFinite(rotation) ? rotation : 0),
        );
      } else if (event.type === "gestureend") {
        safariGesture = undefined;
        setInteracting(false);
      }
    };
    const keyDown = (event: KeyboardEvent) => {
      if (
        event.target !== element ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey
      )
        return;
      const scroll = scrollPosition();
      const step = event.shiftKey ? 200 : 80;
      switch (event.key) {
        case "ArrowLeft":
          pan({ ...scroll, x: scroll.x - step });
          break;
        case "ArrowRight":
          pan({ ...scroll, x: scroll.x + step });
          break;
        case "ArrowUp":
          pan({ ...scroll, y: scroll.y - step });
          break;
        case "ArrowDown":
          pan({ ...scroll, y: scroll.y + step });
          break;
        case "+":
        case "=":
          controls.current.zoom(model.current.zoom + 0.5);
          break;
        case "-":
          controls.current.zoom(model.current.zoom - 0.5);
          break;
        case "0":
        case "Home":
          fit();
          break;
        default:
          return;
      }
      event.preventDefault();
    };
    const preventDrag = (event: DragEvent) => event.preventDefault();
    element.addEventListener("pointerdown", pointerDown);
    window.addEventListener("pointermove", pointerMove);
    window.addEventListener("pointerup", pointerEnd);
    window.addEventListener("pointercancel", pointerEnd);
    element.addEventListener("lostpointercapture", lostCapture);
    element.addEventListener("click", click, true);
    element.addEventListener("dblclick", doubleClick);
    element.addEventListener("wheel", wheel, { passive: false });
    element.addEventListener("keydown", keyDown);
    element.addEventListener("dragstart", preventDrag);
    for (const type of ["gesturestart", "gesturechange", "gestureend"])
      element.addEventListener(type, gesture, { passive: false });
    return () => {
      observer.disconnect();
      element.removeEventListener("scroll", syncCameraScroll);
      element.removeEventListener("pointerdown", pointerDown);
      window.removeEventListener("pointermove", pointerMove);
      window.removeEventListener("pointerup", pointerEnd);
      window.removeEventListener("pointercancel", pointerEnd);
      element.removeEventListener("lostpointercapture", lostCapture);
      element.removeEventListener("click", click, true);
      element.removeEventListener("dblclick", doubleClick);
      element.removeEventListener("wheel", wheel);
      element.removeEventListener("keydown", keyDown);
      element.removeEventListener("dragstart", preventDrag);
      for (const type of ["gesturestart", "gesturechange", "gestureend"])
        element.removeEventListener(type, gesture);
      pointers.forEach((_, id) => {
        if (element.hasPointerCapture(id)) element.releasePointerCapture(id);
      });
      controls.current = {
        zoom: () => {},
        rotate: () => {},
        rotation: () => {},
        tilt: () => {},
        fit: () => {},
        reset: () => {},
        focus: () => {},
        fitRoute: () => {},
        toggleMode: () => {},
      };
    };
  }, [aspect, mapId, maxPins, getDimensions, initialRotation, initialPitch]);

  useLayoutEffect(() => {
    const element = viewport.current;
    const scroll = pendingScroll.current;
    // On mount, measurement schedules the sized stage for the next render.
    // Applying its scroll to the old zero-size stage would clamp it to zero.
    if (!element || !scroll || pendingView.current !== view) return;
    element.scrollLeft = scroll.x;
    element.scrollTop = scroll.y;
    const actual = { x: element.scrollLeft, y: element.scrollTop };
    if (cameraAdjustment.current?.view === view)
      cameraAdjustment.current.scroll = actual;
    cameraScroll.current = actual;
    pendingScroll.current = null;
    pendingView.current = null;
    // The pending position was already used for this render. Reconcile only
    // when native scroll rounding/clamping changed it, not after every frame.
    if (actual.x !== scroll.x || actual.y !== scroll.y)
      setScrollRevision((revision) => revision + 1);
  }, [view]);

  const visibleScroll = pendingScroll.current || cameraScroll.current;
  return {
    viewport,
    viewportBounds: {
      x: visibleScroll.x,
      y: visibleScroll.y,
      width: view.width,
      height: view.height,
    },
    zoom: view.zoom,
    mode: view.mode,
    interacting,
    ...cameraAtScroll(view, getDimensions(view), visibleScroll),
    getView: () => ({
      zoom: model.current.zoom,
      rotation: model.current.rotation,
      pitch: model.current.pitch,
    }),
    changeZoom: (next: number) => controls.current.zoom(next),
    rotate: (delta: number) => controls.current.rotate(delta),
    changeRotation: (angle: number) => controls.current.rotation(angle),
    changePitch: (pitch: number) => controls.current.tilt(pitch),
    fit: () => controls.current.fit(),
    reset: () => controls.current.reset(),
    focus: (location: MapLocation) => controls.current.focus(location),
    fitRoute: (route: VenueMapRouteData, bottomInset = 0) =>
      controls.current.fitRoute(route, bottomInset),
    toggleMode: () => controls.current.toggleMode(),
  };
}
